import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { amazonColors, amazonShadows } from "../../../config/amazonTheme";
import { useCartStore } from "../../../store/useCartStore";
import Price from "../../common/Price";

const ProfileWishlist: React.FC = () => {
  const { t } = useTranslation();
  const { items, updateQuantity, removeFromCart } = useCartStore();

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 99) {
      updateQuantity(productId, newQuantity);
    }
  };

  return (
    <Box className="notranslate" translate="no">
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, mb: 3 }}
        className="notranslate"
        translate="no"
      >
        {t("profile.wishlist")}
      </Typography>

      {items.length === 0 ? (
        <Typography
          variant="body1"
          color="text.secondary"
          className="notranslate"
          translate="no"
        >
          {t("profile.wishlistEmpty")}
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ pb: 2 }}>
          {items.map((item) => (
            <Card
              key={item.cartItemId}
              sx={{ boxShadow: amazonShadows.sm }}
            >
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  {/* Product Image */}
                  <Box sx={{ width: 120, height: 120, flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={
                        item.product.image ||
                        item.product.main_image ||
                        "/placeholder-product.png"
                      }
                      alt={item.product.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                        display: "block",
                      }}
                    />
                  </Box>

                  {/* Product Details */}
                  <Box sx={{ flex: 1, width: "100%" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: amazonColors.text.primary,
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.product.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      className="notranslate"
                      translate="no"
                      sx={{ color: amazonColors.text.secondary, mb: 1 }}
                    >
                      {item.product.brand
                        ? `${t("profile.brand")} ${item.product.brand}`
                        : ""}
                    </Typography>

                    {item.product.condition && (
                      <Chip
                        label={item.product.condition}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}

                    <Typography
                      translate="no"
                      variant="h6"
                      sx={{
                        color: amazonColors.orange,
                        fontWeight: "bold",
                        mb: 0.5,
                      }}
                    >
                      <Price value={item.product.price} />
                    </Typography>
                  </Box>

                  {/* Quantity + Remove Controls (same behavior as cart) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: { xs: "100%", md: "auto" },
                      mt: { xs: 2, md: 0 },
                      justifyContent: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleQuantityChange(
                            item.cartItemId,
                            item.quantity - 1
                          )
                        }
                        disabled={item.quantity <= 1}
                      >
                        <RemoveIcon />
                      </IconButton>

                      <TextField
                        value={item.quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value)) {
                            handleQuantityChange(
                              item.cartItemId,
                              value
                            );
                          }
                        }}
                        size="small"
                        sx={{
                          width: 56,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 40,
                          },
                        }}
                        inputProps={{
                          min: 1,
                          max: 99,
                          inputMode: "numeric",
                          style: { textAlign: "center" },
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() =>
                          handleQuantityChange(
                            item.cartItemId,
                            item.quantity + 1
                          )
                        }
                        disabled={item.quantity >= 99}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>

                    {/* Remove Button */}
                    <IconButton
                      size="small"
                      onClick={() => removeFromCart(item.cartItemId)}
                      sx={{
                        ml: 2,
                        color: amazonColors.red,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ProfileWishlist;
